import {
  hasMeaningfulAnswerType,
  isMinigameType,
} from '@/components/challenges/challengeKinds';
import type {
  ChallengeEditorContext,
  ChallengeEditorMode,
  ChallengeEditorValues,
} from './types';
import {
  MIN_MULTIPLE_CHOICE_OPTIONS,
  MINIGAME_CONFIG_VALIDATORS,
  isAiValidatedAnswerType,
  requiresTextAnswer,
} from './validation';

/** Stable ids for the editor sections (used as DOM anchors + nav keys). */
export const CHALLENGE_EDITOR_SECTION_IDS = [
  'details',
  'content',
  'minigame',
  'location',
  'media',
  'accessCode',
  'gameplay',
  'rewards',
  'voiceover',
] as const;

export type ChallengeEditorSectionId = (typeof CHALLENGE_EDITOR_SECTION_IDS)[number];

/** DOM id for a section card (nav anchors + scrollspy). */
export function sectionAnchorId(id: ChallengeEditorSectionId): string {
  return `challenge-editor-section-${id}`;
}

export interface ChallengeEditorSectionMeta {
  id: ChallengeEditorSectionId;
  /** Card title. */
  title: string;
  /** Short wayfinding label for the nav rail. */
  navLabel: string;
  /** Which editor contexts render this section at all. */
  contexts: readonly ChallengeEditorContext[];
  /** Fine-grained visibility (type-dependent sections, edit-only, …). */
  visible: (values: ChallengeEditorValues, mode: ChallengeEditorMode) => boolean;
  /** "Green dot" — the section's required/possible content is filled in. */
  complete: (values: ChallengeEditorValues) => boolean;
  /** Fields this section owns; used to map validation errors to the nav. */
  errorFields: readonly string[];
}

/** Content section completeness: clue + whatever the answer kind requires. */
function contentComplete(values: ChallengeEditorValues): boolean {
  if (!values.clue.trim()) return false;
  if (!hasMeaningfulAnswerType(values.type)) return true;
  if (isAiValidatedAnswerType(values.answerType)) return !!values.aiContext.trim();
  if (values.answerType === 'multiple_choice') {
    return (
      values.choices.filter((c) => c.trim()).length >= MIN_MULTIPLE_CHOICE_OPTIONS &&
      !!values.answer.trim()
    );
  }
  if (values.answerType === 'true_false') {
    return values.answer === 'true' || values.answer === 'false';
  }
  if (requiresTextAnswer(values.type, values.answerType)) {
    return !!values.answer.trim();
  }
  return true;
}

/** Minigame/puzzle section completeness: playable config exists + validates. */
function minigameComplete(values: ChallengeEditorValues): boolean {
  if (values.type === 'puzzle') return !!values.imageId.trim();
  if (!isMinigameType(values.type)) return true;
  if (values.minigameConfig === null) return false;
  return MINIGAME_CONFIG_VALIDATORS[values.type]?.(values.minigameConfig) === undefined;
}

/** Gameplay completeness: the chosen activation mode is fully configured. */
function gameplayComplete(values: ChallengeEditorValues): boolean {
  if (values.activationMode === 'gps_proximity') {
    return (
      values.latitude !== null &&
      values.longitude !== null &&
      values.activationRadius !== null &&
      values.activationRadius > 0
    );
  }
  if (values.activationMode === 'ibeacon') {
    return !!values.ibeaconUuid.trim();
  }
  return true;
}

const BOTH_CONTEXTS: readonly ChallengeEditorContext[] = ['library', 'event'];
const EVENT_ONLY: readonly ChallengeEditorContext[] = ['event'];

/**
 * Ordered section metadata for the unified challenge editor. Rendering,
 * nav, scrollspy, and error→section mapping all derive from this list —
 * adding a section is a data edit here plus its component.
 */
export const CHALLENGE_EDITOR_SECTIONS: readonly ChallengeEditorSectionMeta[] = [
  {
    id: 'details',
    title: 'Challenge Details',
    navLabel: 'Details',
    contexts: BOTH_CONTEXTS,
    visible: () => true,
    complete: (v) => !!v.name.trim(),
    errorFields: ['name', 'type', 'difficulty', 'points', 'visibility', 'answerType'],
  },
  {
    id: 'content',
    title: 'Content',
    navLabel: 'Content',
    contexts: BOTH_CONTEXTS,
    visible: () => true,
    complete: contentComplete,
    errorFields: [
      'clue',
      'hint',
      'hintCost',
      'answer',
      'alternateAnswers',
      'choices',
      'aiContext',
      'description',
    ],
  },
  {
    id: 'minigame',
    title: 'Minigame Setup',
    navLabel: 'Minigame',
    contexts: BOTH_CONTEXTS,
    visible: (v) => isMinigameType(v.type) || v.type === 'puzzle',
    complete: minigameComplete,
    errorFields: ['minigameConfig', 'puzzleGridSize', 'isTimed'],
  },
  {
    id: 'location',
    title: 'Location',
    navLabel: 'Location',
    contexts: BOTH_CONTEXTS,
    visible: () => true,
    complete: (v) => v.latitude !== null && v.longitude !== null,
    errorFields: ['zone', 'latitude', 'longitude', 'greaterAreaId', 'mapIconColor', 'mapIconImageId'],
  },
  {
    id: 'media',
    title: 'Media',
    navLabel: 'Media',
    contexts: BOTH_CONTEXTS,
    visible: () => true,
    complete: (v) => !!v.imageId.trim() || !!v.videoId.trim(),
    errorFields: ['imageId', 'videoId'],
  },
  {
    id: 'accessCode',
    title: 'Access Code',
    navLabel: 'Access Code',
    contexts: BOTH_CONTEXTS,
    visible: () => true,
    complete: (v) => !!v.accessCode.trim(),
    errorFields: ['accessCode', 'accessCodeMessage'],
  },
  {
    id: 'gameplay',
    title: 'Gameplay',
    navLabel: 'Gameplay',
    contexts: EVENT_ONLY,
    visible: () => true,
    complete: gameplayComplete,
    errorFields: [
      'activationMode',
      'activationRadius',
      'ibeaconUuid',
      'ibeaconMajor',
      'ibeaconMinor',
      'maxCompletions',
      'speedBonuses',
      'requiresFacilitator',
      'facilitatorInstructions',
    ],
  },
  {
    id: 'rewards',
    title: 'Rewards',
    navLabel: 'Rewards',
    contexts: EVENT_ONLY,
    visible: (_v, mode) => mode === 'edit',
    complete: () => true,
    errorFields: [],
  },
  {
    id: 'voiceover',
    title: 'Audio Voiceover',
    navLabel: 'Voiceover',
    contexts: BOTH_CONTEXTS,
    visible: (_v, mode) => mode === 'edit',
    complete: (v) => !!v.audioUrl,
    errorFields: [],
  },
];

/**
 * Resolve the visible, ordered sections for a context + mode + current values.
 *
 * @param context - Library or event editor.
 * @param mode - Create or edit.
 * @param values - Current form values (drives type-dependent visibility).
 * @returns Ordered section metadata to render.
 */
export function getVisibleSections(
  context: ChallengeEditorContext,
  mode: ChallengeEditorMode,
  values: ChallengeEditorValues,
): readonly ChallengeEditorSectionMeta[] {
  return CHALLENGE_EDITOR_SECTIONS.filter(
    (section) => section.contexts.includes(context) && section.visible(values, mode),
  );
}

/**
 * Find the section that owns a validation-error field (for scroll-to-error
 * and the nav's red dot).
 *
 * @param field - Error field key from `validate()`.
 * @returns Owning section metadata, or `undefined` for unknown fields.
 */
export function sectionForField(field: string): ChallengeEditorSectionMeta | undefined {
  return CHALLENGE_EDITOR_SECTIONS.find((section) => section.errorFields.includes(field));
}
